import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Send, CheckCircle, Brain } from 'lucide-react';
import { brandStages } from '../mockData';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    stage: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [quizResult, setQuizResult] = useState(null);

  useEffect(() => {
    // Check if quiz was completed
    const savedQuizResult = sessionStorage.getItem('quizResult');
    if (savedQuizResult) {
      const result = JSON.parse(savedQuizResult);
      setQuizResult(result);
      
      // Pre-fill message with quiz results
      const quizMessage = `Hi! I just completed your Brand Quiz and got the "${result.title}" result. I'm interested in: ${result.recommendedServices.join(', ')}. My budget is around ${result.investment} and I'm looking at a ${result.timeline} timeline.`;
      
      setFormData(prev => ({
        ...prev,
        message: quizMessage
      }));
      
      // Clear from session storage
      sessionStorage.removeItem('quizResult');
      
      // Show toast
      toast.success('Quiz results added to your message!');
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Prepare submission data
      const submissionData = {
        ...formData,
        quiz_result: quizResult || null
      };

      // Call backend API
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData)
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      const result = await response.json();
      console.log('Form submitted successfully:', result);
      
      // Show success state
      setIsSubmitted(true);
      toast.success('Consultation request received! Check your email for confirmation.');
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          company: '',
          stage: '',
          message: ''
        });
        setIsSubmitted(false);
        setQuizResult(null);
      }, 3000);
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to submit form. Please try again.');
    }
  };

  return (
    <section id="contact" className="relative py-24 overflow-hidden">
      {/* Background with gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-purple-950/20 to-slate-950"></div>
      
      {/* Animated gradient overlay */}
      <motion.div
        animate={{
          background: [
            'radial-gradient(circle at 20% 50%, rgba(0,245,255,0.15) 0%, transparent 50%)',
            'radial-gradient(circle at 80% 50%, rgba(168,85,247,0.15) 0%, transparent 50%)',
            'radial-gradient(circle at 20% 50%, rgba(0,245,255,0.15) 0%, transparent 50%)'
          ]
        }}
        transition={{ duration: 10, repeat: Infinity }}
        className="absolute inset-0"
      ></motion.div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-8 sm:mb-12 px-4"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-purple-500 to-blue-600 mb-4">
            Ready to Cultivate a Brand That Leads the Future?
          </h2>
          <div className="w-24 sm:w-32 h-1 bg-gradient-to-r from-cyan-500 to-purple-600 mx-auto mb-4 sm:mb-6 neon-glow"></div>
          <p className="text-base sm:text-lg text-gray-400">
            Let's transform your vision into market dominance
          </p>
          {quizResult && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="mt-4 sm:mt-6 inline-flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 bg-blue-500/20 border border-blue-500/50 rounded-full"
            >
              <Brain size={18} className="text-blue-400 flex-shrink-0" />
              <span className="text-blue-400 font-semibold text-sm sm:text-base">
                Quiz Result: {quizResult.title}
              </span>
            </motion.div>
          )}
        </motion.div>

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="relative"
        >
          <div className="p-6 sm:p-8 lg:p-12 bg-gradient-to-br from-slate-900/80 to-slate-800/80 rounded-3xl border border-cyan-500/30 backdrop-blur-sm">
            {/* Success State */}
            {isSubmitted ? (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="text-center py-8 sm:py-12"
              >
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 0.5 }}
                  className="inline-block mb-4 sm:mb-6"
                >
                  <CheckCircle size={60} className="sm:w-20 sm:h-20 text-green-500" />
                </motion.div>
                <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3 sm:mb-4">
                  Consultation Booked!
                </h3>
                <p className="text-base sm:text-xl text-gray-400">
                  We'll reach out within 24 hours to start building your brand's future.
                </p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name */}
                <div>
                  <label htmlFor="name" className="block text-gray-300 font-semibold mb-2">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-slate-800/50 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all"
                    placeholder="John Doe"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-gray-300 font-semibold mb-2">
                    Business Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-slate-800/50 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all"
                    placeholder="john@company.com"
                  />
                </div>

                {/* Company Name */}
                <div>
                  <label htmlFor="company" className="block text-gray-300 font-semibold mb-2">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    required
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-slate-800/50 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all"
                    placeholder="Your Company Inc."
                  />
                </div>

                {/* Brand Stage */}
                <div>
                  <label htmlFor="stage" className="block text-gray-300 font-semibold mb-2">
                    What stage is your brand in? *
                  </label>
                  <select
                    id="stage"
                    name="stage"
                    required
                    value={formData.stage}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-slate-800/50 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all"
                  >
                    <option value="">Select your stage</option>
                    {brandStages.map((stage) => (
                      <option key={stage} value={stage}>
                        {stage}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-gray-300 font-semibold mb-2">
                    Tell us about your vision
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows="5"
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-slate-800/50 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all resize-none"
                    placeholder="Share your goals, challenges, and what success looks like for your brand..."
                  ></textarea>
                </div>

                {/* Submit Button */}
                <motion.button
                  type="submit"
                  whileHover={{ scale: 1.02, boxShadow: '0 0 40px rgba(0,245,255,0.8)' }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full px-6 py-3 sm:px-8 sm:py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-base sm:text-lg font-bold rounded-lg neon-glow-strong pulse-animation flex items-center justify-center gap-3"
                >
                  <Send size={18} className="flex-shrink-0" />
                  Submit & Book Consultation
                </motion.button>
              </form>
            )}

            {/* Decorative corners */}
            <div className="absolute top-0 left-0 w-32 h-32 border-l-2 border-t-2 border-cyan-500/30 rounded-tl-3xl"></div>
            <div className="absolute bottom-0 right-0 w-32 h-32 border-r-2 border-b-2 border-purple-500/30 rounded-br-3xl"></div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
